'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import HighlightedText from '@/components/HighlightedText/HighlightedText';
import { useIsClient } from '@/hooks';

/**
 * Props for `FeatureHighlightWithListAndImage`.
 */
export type FeatureHighlightWithListAndImageProps =
  SliceComponentProps<Content.FeatureHighlightWithListAndImageSlice>;

/**
 * Component for "FeatureHighlightWithListAndImage" Slices.
 * Features a two-column grid of highlighted issues, introductory titles/descriptions,
 * followed by a vertical list of benefits with optional image.
 */
const FeatureHighlightWithListAndImage: FC<
  FeatureHighlightWithListAndImageProps
> = ({ slice }) => {
  const {
    intro_title,
    intro_description,
    problem_grid,
    section_title,
    section_subtitle,
    list_intro_description,
    benefit_list,
    side_image,
  } = slice.primary;

  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const listSectionRef = useRef<HTMLDivElement>(null);
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const headerEl = headerRef.current;
    const gridEl = gridRef.current;
    const listSectionEl = listSectionRef.current;

    if (headerEl) {
      headerEl.style.transitionDelay = '0ms';
      observer.observe(headerEl);
    }

    if (gridEl) {
      gridEl.style.transitionDelay = '200ms';
      observer.observe(gridEl);
    }

    if (listSectionEl) {
      listSectionEl.style.transitionDelay = '400ms';
      observer.observe(listSectionEl);
    }

    return () => {
      if (headerEl) {
        observer.unobserve(headerEl);
      }
      if (gridEl) {
        observer.unobserve(gridEl);
      }
      if (listSectionEl) {
        observer.unobserve(listSectionEl);
      }
    };
  }, [isClient]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-black px-5 md:px-16 py-[30px]"
    >
      <div className="bg-white rounded-3xl px-5 md:px-16 lg:px-[100px] py-[60px] max-w-[1040px] mx-auto">
        <div className="flex flex-col gap-[100px] items-center">
          {/* First Section - Intro Title, Description, and Problem Grid */}
          <div className="flex flex-col gap-6 items-center text-[#041018] w-full">
            {/* Intro Title with highlight support */}
            <div
              ref={headerRef}
              className="opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              {isFilled.richText(intro_title) && (
                <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-center capitalize">
                  <HighlightedText
                    text={asText(intro_title) || ''}
                    highlightClassName="text-[#069183]"
                  />
                </h2>
              )}
            </div>

            {/* Intro Description */}
            {isFilled.richText(intro_description) && (
              <PrismicRichText
                field={intro_description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-lg font-light leading-[1.4] text-center">
                      {children}
                    </p>
                  ),
                }}
              />
            )}

            {/* Problem Grid - 2x2 layout */}
            {problem_grid && problem_grid.length > 0 && (
              <div
                ref={gridRef}
                className="flex flex-wrap gap-6 items-start w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
              >
                {problem_grid.map((problem, index) => {
                  // Alternate background colors in checkerboard pattern
                  const row = Math.floor(index / 2);
                  const col = index % 2;
                  const isGreen =
                    (row % 2 === 0 && col === 1) ||
                    (row % 2 === 1 && col === 0);
                  const bgColor = isGreen ? 'bg-[#f4fcf7]' : 'bg-white';
                  const borderColor = isGreen
                    ? 'border-white'
                    : 'border-[#dedee9]';

                  return (
                    <div
                      key={index}
                      className={`flex flex-col gap-2 p-4 rounded-xl shadow-[0px_4px_12px_0px_rgba(13,10,44,0.06)] border w-full md:w-[calc(50%-12px)] ${bgColor} ${borderColor}`}
                    >
                      {/* Problem Title */}
                      {problem.problem_title && (
                        <h3 className="text-xl font-semibold leading-[1.2] capitalize">
                          {problem.problem_title}
                        </h3>
                      )}

                      {/* Problem Description */}
                      {problem.problem_description && (
                        <p className="text-base font-light leading-[1.4]">
                          {problem.problem_description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Second Section - Benefits List with Image */}
          <div
            ref={listSectionRef}
            className="flex flex-col lg:flex-row gap-10 items-center w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {/* Left Content - Title, Subtitle, Description, and Benefits List */}
            <div className="flex-1 flex flex-col gap-6 items-start">
              {/* Section Title with highlight support */}
              {isFilled.richText(section_title) && (
                <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] capitalize text-[#041018]">
                  <HighlightedText
                    text={asText(section_title) || ''}
                    highlightClassName="text-[#069183]"
                  />
                </h2>
              )}

              {/* Section Subtitle */}
              {isFilled.richText(section_subtitle) && (
                <PrismicRichText
                  field={section_subtitle}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-[#041018]">
                        {children}
                      </p>
                    ),
                  }}
                />
              )}

              {/* List Intro Description */}
              {isFilled.richText(list_intro_description) && (
                <PrismicRichText
                  field={list_intro_description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-lg font-light leading-[1.4] text-[#041018]">
                        {children}
                      </p>
                    ),
                  }}
                />
              )}

              {/* Benefits List */}
              {benefit_list && benefit_list.length > 0 && (
                <div className="flex flex-col gap-6 items-start w-full">
                  {benefit_list.map((benefit, index) => (
                    <div
                      key={index}
                      className="border-l border-[#cccccc] pl-6 py-2 flex flex-col gap-2 w-full"
                    >
                      {/* Benefit Title */}
                      {benefit.benefit_title && (
                        <h3 className="text-xl font-semibold leading-[1.2] capitalize text-[#041018]">
                          {benefit.benefit_title}
                        </h3>
                      )}

                      {/* Benefit Description */}
                      {benefit.benefit_description && (
                        <p className="text-base font-light leading-[1.4] text-[#041018]">
                          {benefit.benefit_description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Content - Side Image */}
            {isFilled.image(side_image) && (
              <div className="flex-1 relative">
                <PrismicNextImage
                  field={side_image}
                  className="w-full h-auto object-contain rounded-3xl"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureHighlightWithListAndImage;
